/**
 * Downloading code on demand without ever recording a failed download.
 *
 * A browser remembers a failed module download for the rest of the page's
 * life: once a panel's file failed while the connection was down, it kept
 * failing after the connection came back, until the page was reloaded. So a
 * download only starts while the app's server answers; otherwise the load
 * fails straight away and can simply be tried again later.
 */

const PROBE_TIMEOUT_MS = 5000;
// a check this recent is trusted, so loads that start together share one request
const PROBE_FRESH_MS = 5000;

let reachableAt = 0;
let probe: Promise<boolean> | null = null;

/** Whether the app's own server answers right now. */
export function isServerReachable(): Promise<boolean> {
  if (!navigator.onLine) return Promise.resolve(false);
  if (Date.now() - reachableAt < PROBE_FRESH_MS) return Promise.resolve(true);
  probe ??= fetch(import.meta.env.BASE_URL, {
    method: 'HEAD',
    cache: 'no-store',
    signal: AbortSignal.timeout(PROBE_TIMEOUT_MS)
  })
    .then(
      response => response.ok,
      () => false
    )
    .then(ok => {
      if (ok) reachableAt = Date.now();
      probe = null;
      return ok;
    });
  return probe;
}

/** Run `load` only while the app's server answers; otherwise fail without trying. */
export async function loadIfReachable<T>(load: () => Promise<T>): Promise<T> {
  if (!(await isServerReachable())) throw offlineError();
  return load();
}

/**
 * Same, checking only that the browser is online. For page loads, where the
 * page itself just came from the server and an extra request would only add
 * to start-up time.
 */
export function loadIfOnline<T>(load: () => Promise<T>): Promise<T> {
  return navigator.onLine ? load() : Promise.reject(offlineError());
}

/** Resolves now when online, otherwise when the connection comes back. */
export function whenOnline(): Promise<void> {
  if (navigator.onLine) return Promise.resolve();
  return new Promise(resolve => {
    window.addEventListener('online', () => resolve(), { once: true });
  });
}

/*
 * Helpers
 */

function offlineError(): Error {
  return new Error('Offline: on-demand code was not downloaded');
}
